/**
 * 图片加载工具
 *
 * 提供联网图片搜索、占位图生成、懒加载等功能。
 * 参考大厂最佳实践：渐进加载 + 错误兜底 + CDN优化
 */

/** Unsplash 图片搜索（带关键词） */
export const getUnsplashImage = (keyword: string, width = 400, height = 300): string => {
  const encoded = encodeURIComponent(keyword);
  return `https://source.unsplash.com/${width}x${height}/?${encoded}`;
};

/** Picsum 随机图片（作为兜底） */
export const getPicsumImage = (id?: number, width = 400, height = 300): string => {
  const seed = id ?? Math.floor(Math.random() * 1000);
  return `https://picsum.photos/seed/${seed}/${width}/${height}`;
};

/** 根据关键词智能获取图片 */
export const getImageByKeyword = (keyword: string): string => {
  const keywordMap: Record<string, string> = {
    'Logo设计': 'logo,brand,design',
    '活动策划': 'event,planning,conference',
    '品牌视觉': 'branding,visual,creative',
    '海报设计': 'poster,design,graphic',
    '文创产品设计': 'creative,craft,design',
    'IP形象设计': 'character,mascot,cartoon',
    'default': 'creative,design,art',
  };
  const searchTerm = keywordMap[keyword] || keywordMap['default'];
  return getUnsplashImage(searchTerm);
};

/** 生成渐变色占位图SVG */
export const generateGradientPlaceholder = (color1: string, color2: string): string => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300">
    <defs>
      <linearGradient id="g" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" style="stop-color:${color1};stop-opacity:0.15" />
        <stop offset="100%" style="stop-color:${color2};stop-opacity:0.15" />
      </linearGradient>
    </defs>
    <rect width="400" height="300" fill="url(#g)" />
  </svg>`;
  return `data:image/svg+xml;base64,${btoa(svg)}`;
};

/** 图片加载错误时的回调 */
export const handleImageError = (
  e: { target: HTMLImageElement },
  fallbackUrl?: string
): void => {
  const target = e.target;
  if (fallbackUrl && target.src !== fallbackUrl) {
    target.src = fallbackUrl;
  } else {
    target.src = generateGradientPlaceholder('#2997FF', '#64D2FF');
  }
};

/** 预加载图片 */
export const preloadImage = (src: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = reject;
    img.src = src;
  });
};

/** 社区展示图片映射 */
export const communityImages: Record<string, string> = {
  '北冥水世界海洋馆Logo': 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=400&h=300&fit=crop',
  '穿越山海文旅活动全案': 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=300&fit=crop',
  '若雪湄品牌视觉系统': 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=400&h=300&fit=crop',
  'ZEBRA咖啡品牌设计': 'https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=400&h=300&fit=crop',
  '大湾村DA WAN CUN 文创': 'https://images.unsplash.com/photo-1513475382585-d06e58bcb0e0?w=400&h=300&fit=crop',
  'CHILLTIME夏日冰品海报': 'https://images.unsplash.com/photo-1567620905732-2d1ec7ab7445?w=400&h=300&fit=crop',
  '鹿比的家Lubi IP形象': 'https://images.unsplash.com/photo-1558618666-fcd25c85f82e?w=400&h=300&fit=crop',
  '远山文旅视觉升级方案': 'https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?w=400&h=300&fit=crop',
};
